// Level-2 kinds capture (10/18): kind chips on a category page and on /taste, and "More <kind>?" after two saves of the
// same kind. Kinds ride on listing tags as `sub:<slug>` (pipeline/subs.py). Every answer goes through the existing taste
// event the "still into X?" question uses (`signal {kind: 'still_into', interest: 'sub:<slug>', answer}`), so the ranker
// learns it with no other change (rank.ts tasteVector seeds a yes at full weight; a no halves items that carry it).
// What this phone already answered is kept locally so a chip shows its state and a question is never asked twice.
import { useSyncExternalStore } from 'react'
import { signal } from './tasteFeedback'
import { dropBrowseCache, markBrowseStale } from './browseCache'

const KEY = 'mg_kind_answers' // { [slug]: 'yes' | 'no' }
const SAVES = 'mg_kind_saves' // { [slug]: item ids saved with that kind }
const DISMISSED = 'mg_kind_dismissed'
const ASK_AFTER = 2

const read = <T,>(k: string, d: T): T => { try { return JSON.parse(localStorage.getItem(k) ?? '') as T } catch { return d } }
const write = (k: string, v: unknown) => { try { localStorage.setItem(k, JSON.stringify(v)) } catch { /* private mode: answers still reach the server */ } }

export { kindsIn, kindsOf } from './kindsCore'
import { kindsOf } from './kindsCore'

// ---- answers store (one external store so chips and the ask bar stay in step) ----
let answers: Record<string, 'yes' | 'no'> = read(KEY, {})
let asking: string | null = null
let dismissed: string[] = read(DISMISSED, [])
const subs = new Set<() => void>()
let snap: { answers: Record<string, "yes" | "no">; asking: string | null } = { answers, asking }
const emit = () => { snap = { answers, asking }; subs.forEach((f) => f()) }
export function useKinds() {
  return useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f) }, () => snap, () => snap)
}

/** B-216: the kinds this phone knows they like: ones they said yes to, then ones they saved (most saves first). Never a
 *  kind they said no to. */
export function likedKinds(): string[] {
  const saves = read<Record<string, string[]>>(SAVES, {})
  const yes = Object.entries(answers).filter(([, a]) => a === 'yes').map(([k]) => k)
  const saved = Object.entries(saves).sort((a, b) => b[1].length - a[1].length).map(([k]) => k)
  return [...new Set([...yes, ...saved])].filter((k) => answers[k] !== 'no')
}

/** Record a kind answer: the taste event first; the local state only once the server took it. */
export async function answerKind(slug: string, answer: 'yes' | 'no', source: 'kind_chip' | 'kind_ask' | 'taste_kinds'): Promise<void> {
  await signal({ kind: 'still_into', interest: `sub:${slug}`, answer, source })
  answers = { ...answers, [slug]: answer }
  write(KEY, answers)
  if (asking === slug) asking = null
  emit()
  dropBrowseCache(); markBrowseStale() // the next Browse view asks again, in the new order
}

/** After a save: when this is the 2nd save of a kind they have never answered, ask "More <kind>?". */
export function noteSaveKinds(item: { id: string; tags?: readonly string[] | null }) {
  const saves = read<Record<string, string[]>>(SAVES, {})
  let ask: string | null = null
  for (const k of kindsOf(item.tags)) {
    const ids = new Set(saves[k] ?? []); ids.add(item.id)
    saves[k] = [...ids].slice(-20)
    if (!ask && ids.size >= ASK_AFTER && !answers[k] && !dismissed.includes(k)) ask = k
  }
  write(SAVES, saves)
  if (ask && ask !== asking) { asking = ask; emit() }
}
/** Closed without an answer: not asked again on this phone (a close is not a no; nothing is sent). */
export function dismissAsk() { if (asking) { dismissed = [...dismissed, asking]; write(DISMISSED, dismissed) } asking = null; emit() }
